import { AdminField } from 'components/shared/dropdowns'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
} from '@forgeglobal/design-framework'
import { CampaignFormShape } from 'components/campaigns/CampaignForm'
import { Field, useFormState } from 'react-final-form'
import { FundingRound, ValuationType } from 'graphql/bindings/AdminBindings'
import {
  formatAbbreviatedNumber,
  formatMoneyToPrecision,
  formatNumberComma,
  formatPercent,
  formatSharePrice,
  parseNumber,
} from 'util/NumberUtils'
import React from 'react'
import moment from 'moment'

const dashIfZero = (value: number | null | undefined, format: (value: number) => string) =>
  !value || value === 0 ? '-' : format(value)

const TextRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <Box display="flex" paddingTop={2}>
      <Box flex="1 0 200px">{label}</Box>
      <Box flex="5">{value}</Box>
    </Box>
  )
}

const RadioField: React.FC<{ name: string; label: string; value: string; disabled?: boolean }> = ({
  name,
  label,
  value,
  disabled,
}) => {
  return (
    <Field type="radio" name={name} value={value}>
      {fieldProps => {
        const { checked, onChange, ...rest } = fieldProps.input
        return (
          <FormControlLabel
            label={label}
            value={value}
            disabled={disabled}
            checked={checked}
            onChange={onChange}
            control={<Radio inputProps={rest} name={name} />}
          />
        )
      }}
    </Field>
  )
}

export interface CampaignValuationCardProps {
  offeringCapTable: Pick<FundingRound, 'stage' | 'valuation' | 'est_num_shares' | 'price_per_share'> | null | undefined
}

export const CampaignValuationCard: React.FC<CampaignValuationCardProps> = ({ offeringCapTable }) => {
  const {
    values: {
      commission,
      starting_share_price,
      valuation_type,
      est_num_company_shares,
      custom_contact,
      custom_contact_message,
      custom_disclaimer_updated_at,
    },
  } = useFormState<CampaignFormShape>()

  const capTableIsDisabled = !offeringCapTable?.est_num_shares
  const numSharesDisplay =
    valuation_type === ValuationType.CapTable && !capTableIsDisabled
      ? offeringCapTable?.est_num_shares
      : est_num_company_shares
  const valuationDisplay = numSharesDisplay * (starting_share_price ?? 0)
  const feeDisplay = commission ?? 0

  const displayToPrecision = (value: number) => formatMoneyToPrecision(value, 2)

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h6" color="secondary">
            Valuation
          </Typography>
        }
      />
      <CardContent surface="light">
        <RadioField
          name="valuation_type"
          label="Cap Table"
          value={ValuationType.CapTable}
          disabled={capTableIsDisabled}
        />
        <Collapse in={valuation_type === ValuationType.CapTable}>
          <Box paddingLeft={3} paddingRight={3}>
            <Card>
              <CardHeader title="Implied Valuation" />
              <CardContent>
                <TextRow label="Gross" value={dashIfZero(valuationDisplay, displayToPrecision)} />
                <TextRow
                  label={`Net to buyer (${formatPercent(feeDisplay)} fee)`}
                  value={dashIfZero(valuationDisplay * (1 + feeDisplay), displayToPrecision)}
                />
                <TextRow
                  label="Disclaimer"
                  value={
                    custom_disclaimer_updated_at
                      ? moment(custom_disclaimer_updated_at).format('MM/DD/YYYY, h:mm:ss a')
                      : 'default'
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Last Funding Round" />
              <CardContent>
                <TextRow label="Funding Round" value={offeringCapTable?.stage ?? 'Unknown'} />
                <TextRow label="Valuation" value={dashIfZero(offeringCapTable?.valuation, displayToPrecision)} />
                <TextRow
                  label="Outstanding Shares"
                  value={dashIfZero(offeringCapTable?.est_num_shares, formatAbbreviatedNumber)}
                />
                <TextRow
                  label="Price per Share"
                  value={dashIfZero(offeringCapTable?.price_per_share, formatSharePrice)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Disclaimer" />
              <CardContent>
                <Field
                  name="custom_valuation_disclaimer"
                  label="Disclaimer"
                  component={TextField}
                  fullWidth
                  multiline
                />
              </CardContent>
            </Card>
          </Box>
        </Collapse>

        <RadioField name="valuation_type" label="Custom Share Count" value={ValuationType.CustomShareCount} />
        <Collapse in={valuation_type === ValuationType.CustomShareCount}>
          <Box paddingLeft={3} paddingRight={3}>
            <Card>
              <CardHeader title="Implied Valuation" />
              <CardContent>
                <TextRow label="Gross" value={dashIfZero(valuationDisplay, displayToPrecision)} />
                <TextRow
                  label={`Net to buyer (${formatPercent(feeDisplay)} fee)`}
                  value={dashIfZero(valuationDisplay * (1 + feeDisplay), displayToPrecision)}
                />
                <TextRow
                  label="Disclaimer"
                  value={
                    custom_disclaimer_updated_at
                      ? moment(custom_disclaimer_updated_at).format('MM/DD/YYYY, h:mm:ss a')
                      : 'default'
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Custom Outstanding Shares" />
              <CardContent>
                <Field
                  name="est_num_company_shares"
                  label="Enter amount"
                  component={TextField}
                  parse={parseNumber}
                  format={formatNumberComma}
                  fullWidth
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Disclaimer" />
              <CardContent>
                <Field
                  name="custom_valuation_disclaimer"
                  label="Disclaimer"
                  component={TextField}
                  fullWidth
                  multiline
                />
              </CardContent>
            </Card>
          </Box>
        </Collapse>

        <RadioField name="valuation_type" label="Custom Text" value={ValuationType.CustomText} />
        <Collapse in={valuation_type === ValuationType.CustomText}>
          <Box paddingLeft={3} paddingRight={3}>
            <Card>
              <CardHeader title="Preview" />
              <CardContent>
                <TextRow label="Custom Contact Message" value={custom_contact_message ?? ''} />
                <TextRow label="Custom Contact Name" value={custom_contact?.label ?? ''} />
                <TextRow
                  label="Disclaimer"
                  value={
                    custom_disclaimer_updated_at
                      ? moment(custom_disclaimer_updated_at).format('MM/DD/YYYY, h:mm:ss a')
                      : 'default'
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Main Messasge" />
              <CardContent>
                <Field name="custom_contact_message" label="Message" component={TextField} fullWidth multiline />
                <AdminField name="custom_contact" label="Point of Contact" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Disclaimer" />
              <CardContent>
                <Field
                  name="custom_valuation_disclaimer"
                  label="Disclaimer"
                  component={TextField}
                  fullWidth
                  multiline
                />
              </CardContent>
            </Card>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}
